import React, { useState } from "react";
import { useKaraoke } from "../../store/KaraokeStore";
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, RotateCcw, SearchX } from "lucide-react";
import { Slider } from "../ui/slider";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "../ui/alert-dialog";

const BottomControls = () => {
  const { isPlaying, togglePlay, playPrev, playNext, currentSong, currentIndex, queue, volume, setVolume, restartSong, presenting, requestReplacement } = useKaraoke();
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);

  const muted = volume === 0;
  const toggleMute = () => setVolume(muted ? 60 : 0);

  // Playback availability based on the "played"/disabled state of queue items
  const hasEnabledSongs = queue.some((s) => !s.played);
  const hasEnabledAhead = queue.some((s, i) => i > currentIndex && !s.played);
  const hasEnabledBehind = queue.some((s, i) => i < currentIndex && !s.played);
  const canPlay = isPlaying || hasEnabledSongs || (currentSong && !currentSong.played);

  return (
    <footer
      className="h-24 border-t border-border bg-surface flex items-center justify-between px-6 shrink-0 gap-8 relative z-20"
      data-testid="bottom-controls"
    >
      {/* Now playing summary */}
      <div className="flex items-center gap-3 min-w-0 flex-1 max-w-xs">
        {currentSong ? (
          <>
            <img src={currentSong.thumbnail} alt="" className="w-14 h-10 object-cover rounded-sm shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate leading-tight">{currentSong.title}</p>
              <p className="text-xs text-secondary truncate font-mono">{currentSong.singer || "Invitado"}</p>
            </div>
          </>
        ) : (
          <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">— En espera —</p>
        )}
      </div>

      {/* Transport */}
      <div className="flex items-center gap-4">
        <button
          onClick={playPrev}
          disabled={!hasEnabledBehind}
          data-testid="prev-button"
          className="w-10 h-10 rounded-full hover:bg-white/10 flex items-center justify-center disabled:opacity-30 active:scale-95 transition-all"
        >
          <SkipBack className="w-5 h-5" fill="currentColor" />
        </button>
        <button
          onClick={togglePlay}
          disabled={!canPlay}
          data-testid="play-pause-button"
          className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:scale-105 active:scale-95 transition-all disabled:opacity-30 glow-pink"
        >
          {isPlaying ? <Pause className="w-6 h-6" fill="currentColor" /> : <Play className="w-6 h-6 ml-0.5" fill="currentColor" />}
        </button>
        <button
          onClick={playNext}
          disabled={!hasEnabledAhead}
          data-testid="next-button"
          className="w-10 h-10 rounded-full hover:bg-white/10 flex items-center justify-center disabled:opacity-30 active:scale-95 transition-all"
        >
          <SkipForward className="w-5 h-5" fill="currentColor" />
        </button>
        <button
          onClick={restartSong}
          disabled={!currentSong || currentSong.played || !!presenting}
          data-testid="restart-button"
          title="Reiniciar canción"
          className="w-10 h-10 rounded-full hover:bg-white/10 flex items-center justify-center disabled:opacity-30 active:scale-95 transition-all text-secondary"
        >
          <RotateCcw className="w-5 h-5" />
        </button>
        <button
          onClick={() => setShowReplaceConfirm(true)}
          disabled={!currentSong}
          data-testid="find-other-version-button"
          title="Buscar otra versión"
          className="w-10 h-10 rounded-full hover:bg-white/10 flex items-center justify-center disabled:opacity-30 active:scale-95 transition-all text-muted-foreground hover:text-primary"
        >
          <SearchX className="w-5 h-5" />
        </button>
      </div>

      <AlertDialog open={showReplaceConfirm} onOpenChange={setShowReplaceConfirm}>
        <AlertDialogContent className="bg-surface border-border" data-testid="replace-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Buscar otra versión</AlertDialogTitle>
            <AlertDialogDescription>
              Esto detendrá la reproducción actual y buscará otra versión de esta canción, conservando al cantante. La cola no se altera.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="replace-cancel-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="replace-confirm-button"
              onClick={() => { requestReplacement(); setShowReplaceConfirm(false); }}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Buscar otra versión
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Volume */}
      <div className="flex items-center gap-3 flex-1 max-w-xs justify-end">
        <button
          onClick={toggleMute}
          data-testid="mute-button"
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </button>
        <Slider
          value={[volume]}
          onValueChange={(v) => setVolume(v[0])}
          max={100}
          step={1}
          data-testid="volume-slider"
          className="w-32"
        />
        <span className="text-xs font-mono text-muted-foreground w-8 text-right">{volume}</span>
      </div>
    </footer>
  );
};

export default BottomControls;
